import {
  INVITE_TTL_HOURS,
  RESET_TTL_HOURS,
  UserRole,
  type AccountLinkSummary,
  type CreatedAccountLink,
  type User,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { needsReauth } from '../api/client.js';
import {
  accountLinkUrl,
  createResetLink,
  inviteUser,
  listAccountLinks,
  listUsers,
  revokeAccountLink,
  updateUser,
} from '../api/users.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Dashboard accounts (knowledge doc sections 15 and 17; P8.4).
 *
 * An admin invites people, changes their roles, disables them, and makes reset
 * links for forgotten passwords. Invitations and resets are one-time links shown
 * **once**, in a panel this page holds in component state — never the query
 * cache, storage or the URL, as with enrollment tokens. Every change asks for the
 * password or code first, because each of them decides who can reach the fleet.
 *
 * Nobody changes their own row here: another admin does, so nobody demotes or
 * disables themselves by a slip.
 */

const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Viewer — sees servers, events and metrics',
  operator: 'Operator — and acknowledges events, bans and unbans',
  admin: 'Admin — everything, including settings and users',
};

const errorText = (caught: unknown, fallback: string): string =>
  needsReauth(caught)
    ? 'That needed a fresh confirmation. Try again.'
    : caught instanceof Error
      ? caught.message
      : fallback;

export function UsersPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const queryClient = useQueryClient();
  const [created, setCreated] = useState<CreatedAccountLink | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: listUsers });
  const links = useQuery({ queryKey: ['account-links'], queryFn: listAccountLinks });

  const afterChange = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['account-links'] });
  };
  const onCreated = (link: CreatedAccountLink): void => {
    setCreated(link);
    afterChange();
  };

  return (
    <div className="page">
      <PageHeader
        title="Users"
        description="Who can sign in to this dashboard, and what each person may do."
      />

      {created !== null && (
        <ShowLinkPanel
          created={created}
          onDismiss={() => {
            setCreated(null);
          }}
        />
      )}

      <InviteCard onCreated={onCreated} />

      <Card title="People">
        {users.isPending && <Loading />}
        {users.isError && (
          <Alert tone="danger" title="Could not load the accounts">
            Try again in a moment.
          </Alert>
        )}
        {users.data !== undefined && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Accounts, their roles and state</caption>
              <thead>
                <tr>
                  <th scope="col">Account</th>
                  <th scope="col">Role</th>
                  <th scope="col">Last sign-in</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.data.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.id === session.user.id}
                    timezone={timezone}
                    onChanged={afterChange}
                    onLinkCreated={onCreated}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Links waiting to be used"
        description="Invitations and reset links that have not been used yet. Withdraw one that went to the wrong person."
      >
        {links.data?.length === 0 && (
          <EmptyState title="None waiting">
            Links appear here until they are used or run out.
          </EmptyState>
        )}
        {links.data !== undefined && links.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Open invitations and reset links</caption>
              <thead>
                <tr>
                  <th scope="col">For</th>
                  <th scope="col">What</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Made by</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {links.data.map((link) => (
                  <LinkRow key={link.id} link={link} timezone={timezone} onRevoked={afterChange} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** The link, once. Copy it and pass it on however you talk to the person. */
function ShowLinkPanel({
  created,
  onDismiss,
}: {
  created: CreatedAccountLink;
  onDismiss: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const url = accountLinkUrl(created.token);
  const invite = created.link.purpose === 'invite';

  return (
    <Card
      title={
        invite ? `Invitation for ${created.link.email}` : `Reset link for ${created.link.email}`
      }
      description={`Copy it now and send it to them. It is stored only as a hash, so this is the only time it can be shown. It works once, for ${String(invite ? INVITE_TTL_HOURS : RESET_TTL_HOURS)} hours.`}
      aside={<span className="badge badge-accent">Shown once</span>}
    >
      <code className="secret-text">{url}</code>
      <div className="row">
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard
              .writeText(url)
              .then(() => {
                setCopied(true);
              })
              .catch(() => {
                // The link is on screen and selectable either way.
                setCopied(false);
              });
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button onClick={onDismiss}>I have sent it</Button>
      </div>
      <p className="muted text-xs">
        Anyone who opens it can {invite ? 'create this account' : 'set this account’s password'}, so
        send it the way you would send a password: to that person only.
      </p>
    </Card>
  );
}

function InviteCard({ onCreated }: { onCreated: (link: CreatedAccountLink) => void }): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => inviteUser(email, role),
    onSuccess: (link) => {
      setEmail('');
      onCreated(link);
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not make the invitation.'));
    },
  });

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (await ensureReauthenticated()) invite.mutate();
  };

  return (
    <Card
      title="Invite someone"
      description="They get a link to choose their own password. Nobody else ever knows it."
    >
      <form onSubmit={(event) => void submit(event)} className="stack">
        <div className="filters">
          <TextField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <SelectField
            label="Role"
            value={role}
            onChange={(event) => {
              const parsed = UserRole.safeParse(event.target.value);
              if (parsed.success) setRole(parsed.data);
            }}
          >
            {UserRole.options.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABELS[option]}
              </option>
            ))}
          </SelectField>
        </div>
        {error !== null && <Alert tone="danger">{error}</Alert>}
        <div className="row">
          <Button type="submit" variant="primary" busy={invite.isPending} busyLabel="Inviting…">
            Make an invitation link
          </Button>
        </div>
      </form>
    </Card>
  );
}

type Pending =
  { kind: 'role'; role: UserRole } | { kind: 'disable' } | { kind: 'enable' } | { kind: 'reset' };

function UserRow({
  user,
  isSelf,
  timezone,
  onChanged,
  onLinkCreated,
}: {
  user: User;
  isSelf: boolean;
  timezone: string;
  onChanged: () => void;
  onLinkCreated: (link: CreatedAccountLink) => void;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const disabled = user.disabledAt !== null;

  const act = useMutation({
    mutationFn: async (what: Pending): Promise<void> => {
      if (what.kind === 'reset') {
        onLinkCreated(await createResetLink(user.id));
        return;
      }
      await updateUser(
        user.id,
        what.kind === 'role' ? { role: what.role } : { disabled: what.kind === 'disable' },
      );
      onChanged();
    },
    onSuccess: () => {
      setPending(null);
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not change the account.'));
    },
  });

  const start = async (what: Pending): Promise<void> => {
    setError(null);
    if (await ensureReauthenticated()) setPending(what);
  };

  const dialog = (what: Pending): { title: string; description: string; confirm: string } => {
    switch (what.kind) {
      case 'role':
        return {
          title: `Make ${user.email} ${what.role === 'admin' ? 'an admin' : `a ${what.role}`}?`,
          description: `${ROLE_LABELS[what.role]}. They are signed out everywhere and sign in again with the new role.`,
          confirm: 'Change the role',
        };
      case 'disable':
        return {
          title: `Disable ${user.email}?`,
          description:
            'They are signed out everywhere and cannot sign in until an admin enables the account again. Their history and assigned events keep their name.',
          confirm: 'Disable the account',
        };
      case 'enable':
        return {
          title: `Enable ${user.email}?`,
          description: 'They can sign in again with their password, at the role shown.',
          confirm: 'Enable the account',
        };
      case 'reset':
        return {
          title: `Make a reset link for ${user.email}?`,
          description:
            'Whoever opens it sets a new password for this account, and every session the account has is signed out. Their authenticator code is still needed to sign in.',
          confirm: 'Make the link',
        };
    }
  };

  return (
    <tr className={disabled ? 'row-done' : undefined}>
      <td>
        {user.email}
        {isSelf && <span className="badge badge-muted"> you</span>}
        <span className="cell-sub">
          {disabled ? 'disabled' : 'active'}
          {user.totpEnabled ? ' · authenticator on' : ' · no authenticator'}
        </span>
      </td>
      <td className="text-sm">
        {isSelf ? (
          user.role
        ) : (
          <SelectField
            label={`Role for ${user.email}`}
            labelHidden
            value={user.role}
            disabled={act.isPending}
            onChange={(event) => {
              const parsed = UserRole.safeParse(event.target.value);
              if (parsed.success && parsed.data !== user.role) {
                void start({ kind: 'role', role: parsed.data });
              }
            }}
          >
            {UserRole.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectField>
        )}
      </td>
      <td
        className="text-sm"
        title={
          user.lastLoginAt === null ? undefined : formatInstant(user.lastLoginAt, { timezone })
        }
      >
        {user.lastLoginAt === null ? (
          <span className="muted">never</span>
        ) : (
          formatRelative(user.lastLoginAt)
        )}
      </td>
      <td>
        {isSelf ? (
          <span className="muted text-xs">Another admin changes your account.</span>
        ) : (
          <div className="row">
            {!disabled && (
              <Button variant="ghost" onClick={() => void start({ kind: 'reset' })}>
                Reset link
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => void start(disabled ? { kind: 'enable' } : { kind: 'disable' })}
            >
              {disabled ? 'Enable' : 'Disable'}
            </Button>
          </div>
        )}
        {error !== null && pending === null && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
        {pending !== null && (
          <ConfirmDialog
            title={dialog(pending).title}
            description={dialog(pending).description}
            confirmLabel={dialog(pending).confirm}
            danger={
              pending.kind === 'disable' || (pending.kind === 'role' && pending.role === 'admin')
            }
            busy={act.isPending}
            error={error}
            onConfirm={() => {
              act.mutate(pending);
            }}
            onCancel={() => {
              setPending(null);
              setError(null);
            }}
          />
        )}
      </td>
    </tr>
  );
}

function LinkRow({
  link,
  timezone,
  onRevoked,
}: {
  link: AccountLinkSummary;
  timezone: string;
  onRevoked: () => void;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: () => revokeAccountLink(link.id),
    onSuccess: () => {
      setConfirming(false);
      onRevoked();
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not withdraw it.'));
    },
  });

  return (
    <tr>
      <td>{link.email}</td>
      <td className="text-sm">
        {link.purpose === 'invite' ? `Invitation as ${link.role ?? ''}` : 'Password reset'}
      </td>
      <td className="text-sm">{formatInstant(link.expiresAt, { timezone })}</td>
      <td className="text-sm">{link.createdByEmail ?? <span className="muted">—</span>}</td>
      <td>
        <Button
          variant="ghost"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          Withdraw
        </Button>
        {confirming && (
          <ConfirmDialog
            title="Withdraw this link?"
            description="It stops working at once. You can make a new one."
            confirmLabel="Withdraw it"
            danger
            busy={revoke.isPending}
            error={error}
            onConfirm={() => {
              revoke.mutate();
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        )}
      </td>
    </tr>
  );
}
